import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { PAGE_TYPES } from '@sparefoot/tracking-events';
import { getStaticViewPixel } from '@sparefoot/react-core/utils/tracking';

import { submitContactForm as submitContactFormAction } from 'store/modules/pages';
import contactSelector from 'store/selectors/contact';

import { Page, Section, Container, Group } from 'components/core/Page';
import { H1, Paragraph } from 'components/core/Typography';
import { ContactForm } from 'components/email/ContactForm';

import './ContactPage.scss';
import usePageViewTracking from 'hooks/usePageViewTracking';

export function ContactPage({
	canonical: canonicalProp,
	actions: actionsProp,
	recaptchaSiteKey: recaptchaSiteKeyProp,
}) {
	const dispatch = useDispatch();
	const storeProps = useSelector(contactSelector);

	const canonical = canonicalProp ?? storeProps.canonical;
	const recaptchaSiteKey =
		recaptchaSiteKeyProp ?? storeProps.recaptchaSiteKey;
	const actions = useMemo(
		() =>
			actionsProp ?? {
				submitContactForm: (...args) =>
					dispatch(submitContactFormAction(...args)),
			},
		[actionsProp, dispatch],
	);

	usePageViewTracking(PAGE_TYPES.CONTACT);

	const [formSubmitted, setFormSubmitted] = useState(false);

	const onSuccess = useCallback((payload) => {
		setFormSubmitted(true);
		return payload;
	}, []);

	return (
		<Page
			className="contact-page"
			trackingPixel={getStaticViewPixel()}
			pageType={PAGE_TYPES.CONTACT}
			title="SelfStorage.com: Find Cheap Storage Units Near You"
			canonical={canonical}
		>
			<Section>
				{!formSubmitted && (
					<Container>
						<H1 size="large">Contact SelfStorage.com</H1>
						<Group>
							<Paragraph>
								If you have a question, complete the form below
								and we'll get in touch with you!
							</Paragraph>
						</Group>
						<ContactForm
							onSubmit={actions.submitContactForm}
							onSuccess={onSuccess}
							recaptchaSiteKey={recaptchaSiteKey}
						/>
					</Container>
				)}
				{formSubmitted && (
					<Container>
						<H1 size="large">
							Thank you for contacting SelfStorage.com.
						</H1>
						<Paragraph>
							We will be in touch with you soon.
						</Paragraph>
					</Container>
				)}
			</Section>
		</Page>
	);
}

ContactPage.propTypes = {
	canonical: PropTypes.string,
	recaptchaSiteKey: PropTypes.string,
	actions: PropTypes.shape({
		submitContactForm: PropTypes.func.isRequired,
	}),
};

export default ContactPage;
